// useApi — the single seam for API data access (US-874 · R-10).
//
// All HTTP funnels through here so shell/feature components never call `fetch`
// directly. The base URL comes from an env var (no hardcoded host — AGENTS.md
// §4) and requests carry the session cookie (Django session auth — AGENTS.md
// §1). This indirection is the seam where a server-state library (e.g. TanStack
// Query) can be adopted later without touching call sites; US-874 deliberately
// adds no such library yet.
import { useMemo } from "react";
import { cacheKey, withCache } from "../api/cache";
import { httpApiError, invalidJsonApiError, networkApiError } from "../api/errors";
import { fixturesEnabled, resolveFixture } from "../fixtures/apiFixtures";

// Single fetch site for the whole app. `path` resolves against the base URL.
// Every failure surfaces as a normalized ApiError (US-883 · R-2). The body
// arrives as a raw value and is serialized here, so the cache layer can key
// on a stable stringify of it (R-5).
const executeRequest = async (path, options = {}) => {
  const base = process.env.REACT_APP_API_BASE_URL || "";
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const { body, ...fetchOptions } = options;

  const init = {
    credentials: "include",
    ...fetchOptions,
    headers: { "Content-Type": "application/json", ...fetchOptions.headers },
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  let response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    // Cancellation via options.signal (R-3) is a caller decision, not an API
    // failure — re-throw the browser's AbortError unwrapped so hooks can
    // distinguish it from ApiError and skip error state on unmount.
    if (error && error.name === "AbortError") throw error;
    throw networkApiError(path, error);
  }

  const text = await response.text();

  if (!response.ok) {
    throw httpApiError(response.status, text, path);
  }

  // Empty bodies (e.g. 204) resolve to null instead of throwing on parse.
  // Any 2xx is a success — 202 (async report contract, US-891) included.
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw invalidJsonApiError(response.status, path);
  }
};

// Routes a request through the client cache when the caller opts in with
// options.cache = { ttl? } (R-5/R-6). No implicit caching: status polling and
// login must always hit the network. With REACT_APP_USE_FIXTURES on, the
// registered fixture substitutes the network behind the same seam (R-7).
const apiRequest = (path, options = {}) => {
  const { cache, ...requestOptions } = options;
  const run = fixturesEnabled()
    ? () => resolveFixture(requestOptions.method, path, requestOptions.body)
    : () => executeRequest(path, requestOptions);
  if (!cache) return run();
  return withCache(cacheKey(requestOptions.method, path, requestOptions.body), run, cache.ttl);
};

const useApi = () =>
  useMemo(
    () => ({
      get: (path, options) => apiRequest(path, { ...options, method: "GET" }),
      post: (path, body, options) => apiRequest(path, { ...options, method: "POST", body }),
    }),
    []
  );

export default useApi;
